<script setup lang="ts">
/**
 * A quoted chat message: a brand bar, the sender's name, the line „time ·
 * channel“ and the quoted text, clamped to `lines` (default 2).
 *
 * One element for both places a message is quoted. In the composer it is the
 * preview of the message being answered: `cancellable` adds a cancel button
 * (`cancel`), usually with `:lines="1"`. Inside a message it shows the
 * original of a reply or a forward: the sender, `time` and `channel` (where
 * the original was written) above the text. `interactive` turns the quote
 * into a button that emits `open` (jump to the original). `#text` replaces the
 * `text` prop, e.g. with the original rendered as Markdown.
 *
 * Presentational only: it holds no message and no state.
 */
import { computed } from 'vue';
import { DismissIcon } from '@blpde/nx-icons';
import NxIconButton from '../IconButton/NxIconButton.vue';

const props = withDefaults(
  defineProps<{
    /** Name of the sender of the quoted message. */
    sender?: string;
    /** When it was written, already formatted. */
    time?: string;
    /** Where it was written (a channel name), for a quote from another chat. */
    channel?: string;
    /** The quoted text; `#text` replaces it. */
    text?: string;
    /** How many lines of the text show before it ends in „…“. */
    lines?: number;
    /** Show a cancel button (the preview in the composer). */
    cancellable?: boolean;
    cancelLabel?: string;
    /** The quote is a button that emits `open`. */
    interactive?: boolean;
  }>(),
  {
    sender: undefined,
    time: undefined,
    channel: undefined,
    text: undefined,
    lines: 2,
    cancellable: false,
    cancelLabel: 'Abbrechen',
    interactive: false,
  }
);

const emit = defineEmits<{
  /** The cancel button was pressed. */
  cancel: [];
  /** An `interactive` quote was activated. */
  open: [event: MouseEvent];
}>();

defineSlots<{
  /** Replaces the `text` prop, for rich content. */
  text?(): unknown;
}>();

/** „time · channel“, whatever of the two is there. */
const meta = computed(() => [props.time, props.channel].filter(Boolean).join(' · '));
const clamp = computed(() => ({ '--chat-quote-lines': String(Math.max(1, props.lines)) }));
</script>

<template>
  <div class="nx-chat-quote" :class="{ 'is-interactive': interactive }" :style="clamp">
    <component
      :is="interactive ? 'button' : 'div'"
      class="nx-chat-quote__body"
      :type="interactive ? 'button' : undefined"
      @click="interactive ? emit('open', $event) : undefined"
    >
      <span v-if="sender || meta" class="nx-chat-quote__head">
        <span v-if="sender" class="nx-chat-quote__sender">{{ sender }}</span>
        <span v-if="meta" class="nx-chat-quote__meta">{{ meta }}</span>
      </span>
      <span v-if="$slots.text || text" class="nx-chat-quote__text">
        <slot name="text">{{ text }}</slot>
      </span>
    </component>
    <NxIconButton
      v-if="cancellable"
      class="nx-chat-quote__cancel"
      height="xs"
      :title="cancelLabel"
      data-testid="nx-chat-quote-cancel"
      @click="emit('cancel')"
    >
      <DismissIcon filled class="nxt-icon-sm" aria-hidden="true" />
    </NxIconButton>
  </div>
</template>

<style scoped>
.nx-chat-quote {
  display: flex;
  align-items: flex-start;
  gap: var(--nxt-space-xs);
  min-width: 0;
  padding: var(--nxt-space-2xs) var(--nxt-space-xs) var(--nxt-space-2xs) var(--nxt-space-sm);
  background: var(--nxt-surface-sunken-bg);
  border-inline-start: calc(var(--nxt-hairline) * 3) solid var(--nxt-brand-bg);
  border-radius: var(--nxt-radius-sm);
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
}
.nx-chat-quote__body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: start;
}
.nx-chat-quote.is-interactive .nx-chat-quote__body {
  cursor: pointer;
}
.nx-chat-quote.is-interactive .nx-chat-quote__body:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
  border-radius: var(--nxt-radius-sm);
}
.nx-chat-quote__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: var(--nxt-space-xs);
  min-width: 0;
}
.nx-chat-quote__sender {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: var(--nxt-font-weight-medium);
  color: var(--nxt-brand-bg);
}
.nx-chat-quote__meta {
  color: var(--nxt-fg-muted);
}
.nx-chat-quote__text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--chat-quote-lines);
  line-clamp: var(--chat-quote-lines);
  overflow: hidden;
  color: var(--nxt-fg-muted);
  overflow-wrap: anywhere;
}
.nx-chat-quote__cancel {
  flex: none;
}
</style>
